// Service to fetch project data for the map
// Connected to Supabase to get data from the admin panel

import { supabase } from '@takt/database'

export interface ProjectLocation {
  id: string
  name: string
  address: string
  coordinates: [number, number] // [lng, lat]
  projectType: string
  description?: string
  year?: string
}

export interface ProjectWithImages {
  id: string
  name: string
  description?: string
  projectType: string
  images: string[]
  address?: string
  year?: string
  created_at?: string
}

class ProjectService {
  /**
   * Fetch all projects with location data for the map
   * Fetches from Supabase projects table
   */
  async getProjectsForMap(): Promise<ProjectLocation[]> {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, address_street, address_lat, address_lng, project_type, description, year')
        .not('address_lat', 'is', null)
        .not('address_lng', 'is', null)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching projects for map:', error)
        return []
      }

      if (!data) {
        return []
      }

      // Transform the data to match ProjectLocation interface
      return data.map(project => this.transformProjectData(project))
    } catch (error) {
      console.error('Error fetching projects for map:', error)
      return []
    }
  }

  /**
   * Fetch all projects with images for gallery display
   * Fetches from Supabase projects table including images from the project-images bucket
   */
  async getAllProjectsWithImages(): Promise<ProjectWithImages[]> {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, description, project_type, images, address_street, year, created_at')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching projects with images:', error)
        return []
      }

      if (!data) {
        return []
      }

      // Transform the data to match ProjectWithImages interface
      return data.map(project => ({
        id: project.id.toString(),
        name: project.name,
        description: project.description,
        projectType: project.project_type || 'Project',
        images: project.images || [], // Array of image URLs from the project-images bucket
        address: project.address_street,
        year: project.year,
        created_at: project.created_at
      }))
    } catch (error) {
      console.error('Error fetching projects with images:', error)
      return []
    }
  }

  /**
   * Transform admin project data to map format
   */
  transformProjectData(adminProject: any): ProjectLocation {
    return {
      id: adminProject.id.toString(),
      name: adminProject.name,
      address: adminProject.address_street || 'Location TBA',
      coordinates: [adminProject.address_lng, adminProject.address_lat],
      projectType: adminProject.project_type || 'Project',
      description: adminProject.description,
      year: adminProject.year
    }
  }
}

export const projectService = new ProjectService()
